import {
  CANCELLATION_NEVER,
  CreateManagedReservationBody,
  EditReservationBody,
  getDateTimeInTimezone,
  getUTCintoTimezone,
  ManagedReservationDetail,
} from '@seiri/shared/common'
import { CreateManagedReservationForm, EditReservationForm } from './reservation-form.typs'

/** What the wysiwyg editor yields for an editor left visually empty. */
const isBlankHtml = (html: string | undefined) => !html || html.replace(/<p>(<br>)?<\/p>/g, '').trim() === ''

/**
 * The manual-reservation form → the HTTP body. The two shapes differ, so the mapping is explicit
 * (docs/validation.md, rule 4):
 *
 * - The pickers yield browser-local wall-clock `Date`s; they are reinterpreted in the **premise's**
 *   timezone and sent as UTC instants (see the Date/Time section of AGENTS.md).
 * - An empty select is `null` in the form and omitted in the body, and so is an empty description.
 * - Without a cancel deadline the customer may not cancel it themself (`-1`), as before the dialog
 *   offered the choice (2026-10-15).
 */
export const toCreateManagedReservationBody = (
  form: CreateManagedReservationForm,
  timezone: string,
): CreateManagedReservationBody => ({
  name: form.name,
  employeeIds: form.employeeIds,
  ...(form.notifyCustomer && { notifyCustomer: true }),
  ...(!isBlankHtml(form.description) && { description: form.description }),
  ...(form.activityId && { activityId: form.activityId }),
  ...(form.placeId && { placeId: form.placeId }),
  ...(form.customerId && { customerId: form.customerId }),
  startDateTime: getDateTimeInTimezone(form.start, timezone).toISOString(),
  endDateTime: getDateTimeInTimezone(form.end, timezone).toISOString(),
  cancellationDeadline: form.cancellationDeadline ?? CANCELLATION_NEVER,
})

/** A stored reservation → the edit form: its UTC times as the premise's wall clock, notify off. */
export const toEditReservationForm = (
  reservation: ManagedReservationDetail,
  timezone: string,
): EditReservationForm => ({
  name: reservation.name,
  start: getUTCintoTimezone(reservation.start, timezone),
  end: getUTCintoTimezone(reservation.end, timezone),
  activityId: reservation.activity?.id ?? null,
  placeId: reservation.place?.id ?? null,
  employeeIds: reservation.assignedEmployees.map(({ id }) => id),
  description: reservation.description,
  notifyCustomer: false,
})

/** The edit form → the whole edit body, times back to UTC in the premise's timezone; a blank editor clears it. */
export const toEditReservationBody = (form: EditReservationForm, timezone: string): EditReservationBody => ({
  name: form.name,
  startDateTime: getDateTimeInTimezone(form.start, timezone).toISOString(),
  endDateTime: getDateTimeInTimezone(form.end, timezone).toISOString(),
  placeId: form.placeId || null,
  activityId: form.activityId || null,
  employeeIds: form.employeeIds,
  description: isBlankHtml(form.description) ? '' : form.description,
  ...(form.notifyCustomer && { notifyCustomer: true }),
})

/**
 * Whether an edit moves the reservation for the customer - its time, place or employees - so the dialog suggests
 * telling them. A changed name or description alone does not.
 */
export const reservationMoved = (before: EditReservationForm, after: EditReservationForm): boolean =>
  before.start.getTime() !== after.start.getTime() ||
  before.end.getTime() !== after.end.getTime() ||
  (before.placeId || null) !== (after.placeId || null) ||
  before.employeeIds.length !== after.employeeIds.length ||
  before.employeeIds.some((id) => !after.employeeIds.includes(id))
